import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { RescomApiService } from '@app/res-common/services/rescom-api.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationApiServiceOption, ReservationServiceOption } from '@app/res-common/models/rescom-service.model';
import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { DateTime } from 'luxon';
import { IDynamicMapApiParams, ResDynamicMapResponse } from '../models/geo-tree.model';
import { ResBkgAirDetail } from '../models/res-air.model';
import { BookingGuest, ResBkgList } from '../models/res-booking.model';
import { IReservationCompany } from '../models/res-cached-data.models';
import { ReservationCruiseItinerary } from '../models/res-cruise.models';
import { IReservationApiSaveCustomRequest, ReservationCustomRequest } from '../models/res-custom-request.models';
import { IReservationApiGetOptions, IResApiPkgEResAllocation, ResPkgEResAllocation, IResApiPkgEResAllocationManageParams } from '../models/res-option.models';
import { IReservationApiAgency, ReservationAgency } from '../models/res-owner.model';
import { IReservationApiPackageInfo, IReservationApiPackageInfoParameters } from "../models/res-package-detail.model";
import { HlTypes, IPackageDayLocationsByProject, IPackageDayProjects, IPackageHighlight, IPackageItineraryDay2AKP, IResApiDayByDayImportFromWebsite, IResApiGetDayByDayTopicItineraryParams, IResApiGetPackageCostSupplierListParams, IResApiGetPackageReportParams, IResApiGetPackageShipSetupParams, IResApiGetPurcahseOrderParams, IResApiManageDayByDayItinerary, IResApiManagePackagePricesSetup, IResApiManagePackageShipSetupParams, IResApiManagePackageSuiteCategoryStatus, IResApiManagePackageTravelDateRelease, IResApiManageTopicPackageParams, IResApiPackageClone, IResApiPackageCloneSlave, IResApiPackageCommunicationEmail, IResApiPackageConnectedPackages, IResApiPackageGetJetSeats, IResApiPackageManageJetSeats, IResApiPackageSwapParams, IResPackageCostBandGetParams, IResPackageCostBandManageParams, IResPackageDisplay, IResPackageSetupSingleSuppManage, IResParamsPackageAllocations, IResPriceNote, IReservationApiBookPackageParams, IReservationApiGetPackageParams, IReservationApiManagePackageAvailabilityParams, IReservationApiManagePackageDepSchedulingParams, IReservationApiManagePackageItineraryDaysParams, IReservationApiManagePackageLinkedPortsParams, IReservationApiManagePackageLinkedToursParams, IReservationApiManagePackageLinkedVoyagesParams, IReservationApiManagePackageOptionsParams, IReservationApiManagePackageParams, IReservationApiManagePackageTravelDatesParams, IReservationApiManagePackageUpgradeParams, IReservationApiManangePackageCanPolicyParams, IReservationApiPackageConfiguratedPrice, IReservationApiPackageOptionCost, IReservationApiPackageOptionPriceParams, IReservationApiPackageOptionPriceTypeAvailibility, IReservationApiPackageOptionPriceTypeAvailibilityParams, IReservationApiPackageUpgradePriceParams, IReservationApiPricesCloneDatesParams, IReservationApiPricesCloneUpgradeParams, IReservationPackageOptionSpecialItem, MarketingStatus, PackageDayLocation, PackageDayProject, PackageHighlight, PackageHighlightType, PackageItineraryDay2AKP, PackageOptionCostBand, IResApiGetPkgMasterSlaveParams, IResApiManagePkgMasterSlaveParams, ResPackage, ResPackageAllocation, ResPackageAvailabilityStatus, ResPackageBookingList, ResPackageCancPolicy, ResPackageCategory, ResPackageChangesHistory, ResPackageCommunicationEmail, ResPackageConnectedPackages, ResPackageCostSupplierList, ResPackageDayByDay, ResPackageDepScheduling, ResPackageDepartureFootnotes, ResPackageDepartureFreeShorex, ResPackageExportListByBooking, ResPackageFootnotes, ResPackageJetSeat, ResPackageJets, ResPackageMasterSlave, ResPackageOperationSpecialist, ResPackageOptionCost, ResPackagePrice, ResPackagePriceHeader, ResPackagePriceSetup, ResPackagePriceValidityPeriod, ResPackageProperty, ResPackagePurchaseOrder, ResPackageInvoice, ResPackageInvoiceTotal, ResPackageSetupTravelDate, ResPackageShipSetup, ResPackageSingleSupp, ResPackageSuiteConfig, ResPackageTopic, ResPackageTravelDateRelease, ResPackageType, ResPackageUpgrade, ResPackageUpgradePrice, ReservationBasePackage, ResPackageTags, ResPackageTagItem, IResApiManagePackageTagParams, IResApiManageMiniPackageParams, ResMiniPackage, IResApiGetPackageMiniPackagesParams, ResPackagePriceSetupDropdown, IResApiSyncPkgECMParams, IResApiGetPackageInvoicesParams, IResPackageInvoicesResult, IResApiGetPackageDmcPriceParams, IResApiManagePackageDmcPriceParams, ResPackageDmcPriceResult } from '../models/res-package.model';
import { IResApiManagePromoPackages } from '../models/res-promo.model';
import { IResPackageSearchParams, IResPackageSelectParams, IResPackageSnglSuppParams as IResPackageSingleSuppParams } from '../models/res-search.models';
import { IReservationApiCreateNewServiceOrOption, ReservationPackageServiceAllocation, ReservationServiceAllocation } from '../models/res-service.models';

@Injectable({
  providedIn: 'root'
})
export class ReservationPackageApiService extends EidosBaseApiService {
  private get rescomApiService(): RescomApiService {
    return this.injector.get(RescomApiService);
  }

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService,
    private injector: Injector) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  isEmpty(val:any) {
    if(val===undefined || val===null) return true
    return false
  }

  public bookPackage(params: IReservationApiBookPackageParams): Observable<any> {
    return this.postDynamicAPI('package/book', params);
  }

  public checkPackageOptionPrices(params: { PackageID: number, PackageTravelDateID?: number }): Observable<IReservationApiPackageConfiguratedPrice[]> {
    return this.getDynamicAPI('package/checkPrices', params).pipe(
      map<DynamicApiResponse, IReservationApiPackageConfiguratedPrice[]>((response) => response.body)
    );
  }

  /**
   * Calls API to clone the package with specified package ID
   *
   * @param {{ PackageID: number, ClonePrice: ReservationApiBoolean }} params
   * @return {*}  {Observable<number>}
   * @memberof ReservationPackageApiService
   */
  clonePackage(params: IResApiPackageClone): Observable<number> {
    return this.postDynamicAPI('package/clone', params).pipe(
      map<DynamicApiResponse, number>((response) => Array.isArray(response.body) && response.body.length > 0 && response.body[0].PackageID ? response.body[0].PackageID : ResPackage.INVALID_PACKAGE_ID)
    );
  }

  clonePackageSlave(params: IResApiPackageCloneSlave): Observable<number> {
    return this.postDynamicAPI('package/clonePkgSlave', params).pipe(
      map<DynamicApiResponse, number>((response) => Array.isArray(response.body) && response.body.length > 0 && response.body[0].PackageID ? response.body[0].PackageID : ResPackage.INVALID_PACKAGE_ID)
    );
  }

  createNewServiceOptionPackage(params: IReservationApiCreateNewServiceOrOption) {
    return this.postDynamicAPI('service/manageServiceOption', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  dayByDayImportFromWebsite(params: IResApiDayByDayImportFromWebsite) {
    return this.postDynamicAPI('package/importDayByDayWebsite', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  dayByDayOperationalImportFromOperational(pkgID: number) {
    return this.postDynamicAPI('package/getPkgOperationalUpd', { PackageID: pkgID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  dayByDayUpdateBkg(params: IResApiDayByDayImportFromWebsite) {
    return this.postDynamicAPI('package/updateDayByDayBkg', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getAccomodationReport(packageID: number): Observable<any> {
    return this.callDynamicAPI('package/getAccomodationReport', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  /**
   * Get Agencies
   *
   * @param {IReservationApiAgency} [filters]
   * @return {*}
   * @memberof ReservationPackageApiService
   */
  public getAgencies(filters?: IReservationApiAgency) {
    return this.callDynamicAPI('agency/select', filters, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgency[]>((response) => {
        return response.body.map((r: IReservationApiAgency) => new ReservationAgency(r));
      })
    );
  }

  getBkgAirDetails(params: { BkgID: number }): Observable<ResBkgAirDetail[]> {
    return this.callDynamicAPI('bkgAirDetails/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResBkgAirDetail[]>((response) =>
        response.body.map((c: any) => new ResBkgAirDetail(c))
      )
    );
  }

  /**
   * Retrieves booking custom request
   *
   * @param {number} bkgID
   * @param {number} sectionID
   * @return {*}
   * @memberof ReservationPackageApiService
   */
  public getBookingCustomRequest(bkgID: number, sectionID: number) {
    return this.postDynamicAPI('customRequest/get', { BkgID: bkgID, SectionID: sectionID }).pipe(
      map<DynamicApiResponse, ReservationCustomRequest | null>((response) => {
        return !this.isEmpty(response.body) ? new ReservationCustomRequest(response.body) : null;
      })
    );
  }

  /**
   * Retrieves booking guests
   *
   * @param {number} bkgID
   * @param {number} sectionID
   * @return {*}
   * @memberof ReservationPackageApiService
   */
  public getBookingGuests(bkgID: number, sectionID: number) {
    return this.postDynamicAPI('booking/guests', { BkgID: bkgID, SectionID: sectionID }).pipe(
      map<DynamicApiResponse, Array<BookingGuest>>((response) => {
        return response.body.map((guest: any) => new BookingGuest(guest));
      })
    );
  }

  /**
   * Retrieves booking itinerary
   *
   * @param {number} bkgID
   * @return {*}
   * @memberof ReservationPackageApiService
   */
  public getBookingItinerary(bkgID: number) {
    return this.postDynamicAPI('booking/itinerary', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, Array<ReservationCruiseItinerary>>((response) => {
        return response.body.map((i: any) => new ReservationCruiseItinerary(i));
      })
    );
  }

  getCommunicationEmail(params: IResApiPackageCommunicationEmail): Observable<ResPackageCommunicationEmail[]> {
    return this.getDynamicAPI('package/getCommunicationEmail', params).pipe(
      map<DynamicApiResponse, ResPackageCommunicationEmail[]>((response) => {
        return response.body.map((data: any) => new ResPackageCommunicationEmail(data))
      })
    );
  }

  getConnectedPackage(params: IResApiPackageConnectedPackages): Observable<ResPackageConnectedPackages[]> {
    return this.getDynamicAPI('package/getConnectedPackages', params).pipe(
      map<DynamicApiResponse, ResPackageConnectedPackages[]>((response) => {
        return response.body.map((data: any) => new ResPackageConnectedPackages(data))
      })
    );
  }

  getDynamicMap(prms: IDynamicMapApiParams) {
    return this.getExternalAPI('oem/keytechmaps/itinerary', 'POST', prms).pipe(
      map<any, ResDynamicMapResponse>(res => new ResDynamicMapResponse(res))
    );
  }

  getExportedPackageTable(params: { PackageID?: number, PackageTravelDateID?: number, BkgStatus?: string }): Observable<ResPackageExportListByBooking[]> {
    return this.getDynamicAPI('bkg/getExportPackageListBooking', params).pipe(
      map<DynamicApiResponse, ResPackageExportListByBooking[]>((response) => {
        return response.body.map((data: any) => new ResPackageExportListByBooking(data))
      })
    );
  }

  getHlTypes(): Observable<HlTypes[]> {
    return this.getDynamicAPI('package/hlTypes').pipe(
      map<DynamicApiResponse, HlTypes[]>((response) => {
        return response.body.map((data: any) => new HlTypes(data))
      })
    );
  }

  getJets(packageTypeID?: number): Observable<ResPackageJets[]> {
    return this.getDynamicAPI('package/getPackageJet', { PackageTypeID: packageTypeID }).pipe(
      map<DynamicApiResponse, ResPackageJets[]>((response) => {
        return response.body.map((data: any) => new ResPackageJets(data))
      })
    );
  }

  getJetSeats(params: IResApiPackageGetJetSeats): Observable<ResPackageJetSeat[]> {
    return this.getDynamicAPI('package/getJetSeats', params).pipe(
      map<DynamicApiResponse, ResPackageJetSeat[]>((response) => {
        const rows = Array.isArray(response.body) ? response.body : (response.body?.DT0 ?? []);
        return rows.map((data: any) => new ResPackageJetSeat(data));
      })
    );
  }

  managePackageJetSeats(params: IResApiPackageManageJetSeats) {
    return this.postDynamicAPI('package/manageJetSeats', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getMarketingStatus(): Observable<MarketingStatus[]> {
    return this.getDynamicAPI('package/marketingStatus').pipe(
      map<DynamicApiResponse, MarketingStatus[]>((response) => {
        return response.body.map((data: any) => new MarketingStatus(data))
      })
    );
  }

  public getOptions(params?: IReservationApiGetOptions): Observable<ReservationServiceOption[]> {
    return this.callDynamicAPI('option/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationServiceOption>>((response) => {
        return response.body.map((o: IReservationApiServiceOption) => new ReservationServiceOption(o));
      })
    );
  }

  public getPackageAllocationStatus(params: IResParamsPackageAllocations): Observable<ResPackageAllocation> {
    return this.callDynamicAPI('product/allocationStatus', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ResPackageAllocation>((response) => new ResPackageAllocation(response.body?.[0]))
      );
  }

  getPackageAvailabilityStatus(): Observable<ResPackageAvailabilityStatus[]> {
    return this.getDynamicAPI('package/PackageAvailabilityStatus').pipe(
      map<DynamicApiResponse, ResPackageAvailabilityStatus[]>((response) => {
        return response.body.map((data: any) => new ResPackageAvailabilityStatus(data))
      })
    );
  }

  public getPackageBookingList(params: { PackageID?: number, PackageTravelDateID?: number | null, BkgStatus?: string }): Observable<ResPackageBookingList[]> {
    return this.callDynamicAPI('package/selectBookingList', params).pipe(
      map<DynamicApiResponse, Array<ResPackageBookingList>>((response) => {
        return response.body.map((p: any) => new ResPackageBookingList(p));
      })
    );
  }

  public getPackageCancPolicy(packageID: number, history = false): Observable<ResPackageCancPolicy[]> {
    return this.callDynamicAPI('package/selectCancPolicy', { packageID, history: history ? 'Y' : 'N' }).pipe(
      map<DynamicApiResponse, ResPackageCancPolicy[]>((response) => {
        return response.body.map((p: any) => new ResPackageCancPolicy(p))
      })
    );
  }

  getPackageCategory(companyID?: number): Observable<ResPackageCategory[]> {
    return this.getDynamicAPI('package/getPackageCategory', { CompanyID: companyID }).pipe(
      map<DynamicApiResponse, ResPackageCategory[]>((response) => {
        return response.body.map((data: any) => new ResPackageCategory(data))
      })
    );
  }

  getPackageChangesHistory(packageID: number): Observable<ResPackageChangesHistory[]> {
    return this.callDynamicAPI('package/changesHistory', { PackageID: packageID }, 'GET').pipe(
      map<DynamicApiResponse, ResPackageChangesHistory[]>((response) =>
        (response.body ?? []).map((data: any) => new ResPackageChangesHistory(data))
      )
    );
  }

  getPackageCostSupplierList(params: IResApiGetPackageCostSupplierListParams): Observable<ResPackageCostSupplierList[]> {
    return this.callDynamicAPI('package/getServiceListSupplierForAPC', params).pipe(
      map<DynamicApiResponse, ResPackageCostSupplierList[]>((response) => response.body.map((data: any) => new ResPackageCostSupplierList(data)))
    );
  }

  getPackageDayLocationsByProjectID(params?: IPackageDayLocationsByProject): Observable<PackageDayLocation[]> {
    return this.getDynamicAPI('package/dayLocationByProject', params).pipe(
      map<DynamicApiResponse, PackageDayLocation[]>((response) => {
        return response.body.map((data: any) => new PackageDayLocation(data))
      })
    );
  }

  getPackageDayProjects(params?: IPackageDayProjects): Observable<PackageDayProject[]> {
    return this.getDynamicAPI('package/dayProject', params).pipe(
      map<DynamicApiResponse, PackageDayProject[]>((response) => {
        return response.body.map((data: any) => new PackageDayProject(data))
      })
    );
  }

  public getPackageDepScheduling(packageID: number, history = false): Observable<ResPackageDepScheduling[]> {
    return this.callDynamicAPI('package/selectDepScheduling', { packageID, history: history ? 'Y' : 'N' }).pipe(
      map<DynamicApiResponse, Array<ResPackageDepScheduling>>((response) => {
        return response.body.map((p: any) => new ResPackageDepScheduling(p));
      })
    );
  }

  getPackageDeparturesFootnotes(): Observable<ResPackageDepartureFootnotes[]> {
    return this.getDynamicAPI('packageDeparture/getFootnote').pipe(
      map<DynamicApiResponse, ResPackageDepartureFootnotes[]>((response) => {
        return response.body.map((data: any) => new ResPackageDepartureFootnotes(data))
      })
    );
  }

  getPackageDeparturesFreeShorex(): Observable<ResPackageDepartureFreeShorex[]> {
    return this.getDynamicAPI('packageDeparture/getFreeShorex').pipe(
      map<DynamicApiResponse, ResPackageDepartureFreeShorex[]>((response) => {
        return response.body.map((data: any) => new ResPackageDepartureFreeShorex(data))
      })
    );
  }

  getPackageFootnote(packageID: number): Observable<ResPackageFootnotes[]> {
    return this.getDynamicAPI('package/getPackageFootnote', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResPackageFootnotes[]>((response) => {
        return response.body.map((data: any) => new ResPackageFootnotes(data))
      })
    );
  }

  getPackageHighlightsList(packageID: number): Observable<PackageHighlight[]> {
    return this.getDynamicAPI('package/highlightsList', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, PackageHighlight[]>((response) => {
        return response.body.map((data: any) => new PackageHighlight(data))
      })
    );
  }

  getPackageHighlightsType(): Observable<PackageHighlightType[]> {
    return this.rescomApiService.getPackageHighlightsType();
  }

  /**
   * Gets data details for a package: ECM, Itineraries and traveldate
   *
   * @param {number} packageID
   * @return {*}  {Observable<IReservationApiPackageInfo>}
   * @memberof ReservationPackageApiService
   */
  getPackageInfo(parameters: IReservationApiPackageInfoParameters): Observable<IReservationApiPackageInfo> {
    return this.getDynamicAPI('packageinfo/get', parameters).pipe(
      map<DynamicApiResponse, IReservationApiPackageInfo>((response) => {
        return response.body as IReservationApiPackageInfo;
      }),
      catchError<any, Observable<IReservationApiPackageInfo>>((_: any) => new Observable<IReservationApiPackageInfo>())
    );
  }

  getPackageInvoices(params: IResApiGetPackageInvoicesParams): Observable<IResPackageInvoicesResult> {
    return this.callDynamicAPI('package/getPackageInvoices', params).pipe(
      map<DynamicApiResponse, IResPackageInvoicesResult>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        const totals = response.body.DT2 ?? []
        return {
          invoices: header.map((v: any) => {
            v.Details = details.filter((d: any) => d.InvoiceID === v.InvoiceID)
            return new ResPackageInvoice(v)
          }),
          totals: totals.map((t: any) => new ResPackageInvoiceTotal(t)),
        }
      })
    );
  }

  getPackageDmcPrice(params: IResApiGetPackageDmcPriceParams): Observable<ResPackageDmcPriceResult> {
    return this.getDynamicAPI('package/GetPackageDMCPrice', params).pipe(
      map<DynamicApiResponse, ResPackageDmcPriceResult>((response) => new ResPackageDmcPriceResult(response.body))
    )
  }

  managePackageDmcPrice(params: IResApiManagePackageDmcPriceParams): Observable<any> {
    return this.postDynamicAPI('package/managePackageDMCPrice', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    )
  }

  getPackageItineraryDays2AKP(packageID: number, packageItineraryDayID: number): Observable<PackageItineraryDay2AKP[]> {
    return this.getDynamicAPI('package/itineraryDay2AKP', { PackageID: packageID, PackageItineraryDayID: packageItineraryDayID }).pipe(
      map<DynamicApiResponse, PackageItineraryDay2AKP[]>((response) => {
        return response.body.map((data: any) => new PackageItineraryDay2AKP(data))
      })
    );
  }

  getPackageMiniPackages(params: IResApiGetPackageMiniPackagesParams): Observable<ResMiniPackage[]> {
    return this.callDynamicAPI('package/getMiniPackages', params).pipe(
      map<DynamicApiResponse, ResMiniPackage[]>((response) => response.body.map((data: any) => new ResMiniPackage(data)))
    );
  }

  getPackageOperationSpecialist(): Observable<ResPackageOperationSpecialist[]> {
    return this.callDynamicAPI('package/getOperationSpecialist').pipe(
      map<DynamicApiResponse, ResPackageOperationSpecialist[]>(
        (response) => response.body.map((data: any) => new ResPackageOperationSpecialist(data))
      ));
  }

  /**
   * Retrieves a package product Optimized
   *
   * @param {IResPackageSelectParams} params
   * @return {*}  {Observable<ReservationBasePackage>}
   * @memberof ReservationPackageApiService
   */
  public getPackageOpt(params: IResPackageSelectParams): Observable<ReservationBasePackage> {
    return this.callDynamicAPI('product/selectPkgOpt', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ReservationBasePackage>((response) => new ReservationBasePackage(response))
      );
  }

  getPackageOptionCostsAndPrices(params: {
    Currency: string,
    PriceTypeID: number,
    CompanyID: number,
    AgencyID: number | undefined,
    PackageTravelDateID: number,
    StartDate: string,
    EndDate: string,
    PackageID: number,
    ForceReload: 'Y' | 'N',
    PackageOption?: IReservationApiManagePackageOptionsParams[]
  }): Observable<[Array<ResPackageOptionCost>, Array<ResPackagePrice>, Array<ResPackageUpgradePrice>]> {
    return this.callDynamicAPI('package/selectOptionCostsAndPrices', params).pipe(
      map<DynamicApiResponse, [Array<ResPackageOptionCost>, Array<ResPackagePrice>, Array<ResPackageUpgradePrice>]>((response) => {
        return [
          response.body.Costs.map((c: any) => new ResPackageOptionCost(c)),
          response.body.Prices.map((c: any) => new ResPackagePrice(c)),
          response.body.UpgradesPrices.map((c: any) => new ResPackageUpgradePrice(c))
        ];
      })
    );
  }

  public getPackageOptionsAvailabilityElements(params: { AvByOption: IReservationApiPackageOptionPriceTypeAvailibilityParams[], AllPriceType: 'Y' | 'N' }): Observable<IReservationApiPackageOptionPriceTypeAvailibility[]> {
    return this.postDynamicAPI('package/availabilityOptions', params).pipe(
      map<DynamicApiResponse, IReservationApiPackageOptionPriceTypeAvailibility[]>((response) => response.body));
  }

  getPackagePhysicalLevels() {
    return this.callDynamicAPI('package/getPhysicalLevels').pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  getPackagePricesSetup(params: { PackageID: number, CompanyIDTM: number, StartDate?: DateTime }): Observable<{ packageHeader: ResPackagePriceHeader; prices: ResPackagePriceSetup[]; travelDates: any[], currencies: string[], priceTypes: ResPackagePriceSetupDropdown[], validityPeriods: ResPackagePriceValidityPeriod[], priceNoteTypes: IResPriceNote[], packageDisplayTypes: IResPackageDisplay[], companies: IReservationCompany[], voyages: { voyageID: number, voyageNumber: string }[] }> {
    return this.getDynamicAPI('pkgPriceSetup/getPackagePrices', params).pipe(
      map<DynamicApiResponse, { packageHeader: ResPackagePriceHeader, prices: ResPackagePriceSetup[], travelDates: any[], currencies: string[], priceTypes: ResPackagePriceSetupDropdown[], validityPeriods: ResPackagePriceValidityPeriod[], priceNoteTypes: IResPriceNote[], packageDisplayTypes: IResPackageDisplay[], companies: IReservationCompany[], voyages: { voyageID: number, voyageNumber: string }[] }>(response => {
        return {
          packageHeader: new ResPackagePriceHeader(response.body.DT0[0]),
          validityPeriods: response.body.DT2.map((data: any) => new ResPackagePriceValidityPeriod(data)),
          travelDates: response.body.DT1.map((data: any) => new ResPackageSetupTravelDate(data)),
          prices: response.body.DT3.map((data: any) => new ResPackagePriceSetup(data)),
          currencies: response.body.DT4.map((data: any) => data.CurrencyCod),
          priceTypes: response.body.DT5.map((data: any) => new ResPackagePriceSetupDropdown(data)),
          priceNoteTypes: response.body.DT6,
          packageDisplayTypes: response.body.DT7,
          companies: response.body.DT8,
          voyages: response.body.DT0.map((data: any) => ({ voyageID: data.VoyageID, voyageNumber: data.VoyageNumber }))
        }
      })
    )
  }

  getPackageProperty(): Observable<ResPackageProperty[]> {
    return this.getDynamicAPI('package/getPackageProperty').pipe(
      map<DynamicApiResponse, ResPackageProperty[]>((response) => {
        return response.body.map((data: any) => new ResPackageProperty(data))
      })
    );
  }

  getPackageReport(params: IResApiGetPackageReportParams): Observable<any> {
    return this.callDynamicAPI('package/getPackageReport', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getPackageServiceAllocations(params: { PackageID: number }): Observable<{ travelDates: { packageTravelDateID: number, travelStartDate: string, travelEndDate: string }[], options: ReservationPackageServiceAllocation[] }> {
    return this.callDynamicAPI('package/getPackageServiceAllocations', params).pipe(
      map<DynamicApiResponse, any>((res) => {
        const options = (res.body.DT0 ?? []).map(
          (el: any) => new ReservationPackageServiceAllocation({
            ...el, packageMaxPax: res.body.DT1?.[0]?.MaxPax
          })
        );
        const travelDates = (res.body.DT2 ?? []).map((el: { PackageTravelDateID: number, TravelStartDate: string, TravelEndDate: string }) => {
          return {
            packageTravelDateID: el.PackageTravelDateID, travelStartDate: el.TravelStartDate, travelEndDate: el.TravelEndDate
          }
        });
        return { options, travelDates }
      })
    )
  }

  getPackageShipSetup(params: IResApiGetPackageShipSetupParams): Observable<{
    packageShipSetup: ResPackageShipSetup[],
    voyages: { voyageID: number, voyageNumber: string }[],
    suites: { suiteID: number, suiteNo: string, suiteNumber: string }[],
    suiteCategories: { suiteCategoryID: number, suiteCategoryCod: string, suiteCategory: string }[]
  }> {
    return this.callDynamicAPI('package/getPackageShip', params).pipe(
      map<DynamicApiResponse, { packageShipSetup: ResPackageShipSetup[], voyages: { voyageID: number, voyageNumber: string }[], suites: { suiteID: number, suiteNo: string, suiteNumber: string }[], suiteCategories: { suiteCategoryID: number, suiteCategoryCod: string, suiteCategory: string }[] }>((response) => {
        return {
          packageShipSetup: response.body.DT0.map((data: any) => new ResPackageShipSetup(data)),
          voyages: response.body.DT1.map((data: any) => ({ voyageID: data.VoyageID, voyageNumber: data.VoyageNumber })),
          suites: response.body.DT2.map((data: any) => ({ suiteID: data.SuiteID, suiteNo: data.SuiteNo, suiteNumber: data.SuiteNumber })),
          suiteCategories: response.body.DT3.map((data: any) => ({ suiteCategoryID: data.SuiteCategoryID, suiteCategoryCod: data.SuiteCategoryCod, suiteCategory: data.SuiteCategory }))
        }
      })
    );
  }

  /**
   *
   * Retrieves a package single supplement cost
   *
   * @param {IResPackageSearchParams} params
   * @return {*}  {Observable<BookingPackage>}
   * @memberof ReservationPackageApiService
   */
  public getPackageSingleSupp(params: IResPackageSingleSuppParams): Observable<ResPackageSingleSupp[]> {

    return this.callDynamicAPI(
      'package/selectSingleSupplement',
      params,
      'GET'
    ).pipe(
      map<DynamicApiResponse, ResPackageSingleSupp[]>(
        (response) => response.body.map((pkg: any) => new ResPackageSingleSupp(pkg))
      )
    );
  }

  public getPackageSpecialOptions(): Observable<Array<IReservationPackageOptionSpecialItem>> {
    return this.callDynamicAPI('package/selectSpecialOptions').pipe(
      map<DynamicApiResponse, Array<IReservationPackageOptionSpecialItem>>((response) => {
        return response.body as IReservationPackageOptionSpecialItem[];
      })
    );
  }

  getPackageTag(packageID: number): Observable<ResPackageTagItem[]> {
    return this.callDynamicAPI('package/getPackageTag', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResPackageTagItem[]>((response) => response.body.map((data: any) => new ResPackageTagItem(data)))
    );
  }

  getPackageTags(): Observable<ResPackageTags[]> {
    return this.callDynamicAPI('package/getPackageGeneralScope').pipe(
      map<DynamicApiResponse, ResPackageTags[]>((response) => response.body.map((data: any) => new ResPackageTags(data)))
    );
  }

  getPackageTravelDateRelease(packageID: number, packageTravelDateID: number): Observable<{ status: string, releaseData: ResPackageTravelDateRelease[] }> {
    return this.getDynamicAPI('package/getTravelDateRelease', { PackageID: packageID, PackageTravelDateID: packageTravelDateID }).pipe(
      map<DynamicApiResponse, { status: string, releaseData: ResPackageTravelDateRelease[] }>((response) => {
        return {
          status: response.body.DT0[0]?.AvailabilityStatusCod,
          releaseData: response.body.DT1.map((data: any) => new ResPackageTravelDateRelease(data))
        }
      })
    );
  }

  getPackageTypeForSetup(): Observable<ResPackageType[]> {
    return this.rescomApiService.getPackageTypeForSetup();
  }

  getPackageUpgrades(params: { BkgID: number }): Observable<ResPackageUpgrade[]> {
    return this.callDynamicAPI('packageUpgrade/select', params).pipe(
      map<DynamicApiResponse, Array<ResPackageUpgrade>>((response) => {
        return response.body.map((p: any) => new ResPackageUpgrade(p));
      })
    );
  }

  getPackageVoyageSuites(packageID: number, packageTravelDateID: number): Observable<ResPackageSuiteConfig[]> {
    return this.rescomApiService.getPackageVoyageSuites(packageID, packageTravelDateID);
  }

  public getPackages(params: IReservationApiGetPackageParams): Observable<ResPackage[]> {
    return this.rescomApiService.getPackages(params);
  }

  public getPackagesPaginated(params?: IReservationApiGetPackageParams, loadOptions?: any): Promise<ResolvedData<ResPackage>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.callDynamicAPI('packageHeader/select', params)).then((response: any) => {
      return {
        data: response.body.map((pkg: any) => new ResPackage(pkg)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  public getPkgEResAllocation(params: IResApiPkgEResAllocation): Observable<ResPkgEResAllocation[]> {
    return this.callDynamicAPI('package-setup/eres-alloc', params).pipe(
      map<DynamicApiResponse, ResPkgEResAllocation[]>((response) => {
        return response.body.map((p: any) => new ResPkgEResAllocation(p));
      })
    );
  }

  getPkgMasterSlave(params: IResApiGetPkgMasterSlaveParams): Observable<ResPackageMasterSlave[]> {
    return this.callDynamicAPI('package/getPkgMasterSlave', params).pipe(
      map<DynamicApiResponse, ResPackageMasterSlave[]>((response) => {
        return response.body.map((data: any) => new ResPackageMasterSlave(data))
      })
    )
  }

  public getPkgSearchCK(params: IResPackageSearchParams): Observable<ResPackage[]> {
    return this.callDynamicAPI(
      'package/getPkgSearchCK',
      params,
      'GET'
    ).pipe(
      map<DynamicApiResponse, ResPackage[]>((response) => {
        const packages = response.body?.DT0 ?? response.body ?? [];
        const travelDates = response.body?.DT1 ?? [];

        return packages.map((pkg: any) => {
          const packageID = pkg.PackageID ?? 0;
          const packageTravelDates = travelDates.filter((travelDate: any) => (travelDate.PackageID ?? 0) === packageID);

          return new ResPackage({
            ...pkg,
            TravelDates: packageTravelDates
          });
        });
      })
    );
  }

  getPkgSlave(params: IResApiGetPkgMasterSlaveParams): Observable<ResPackageMasterSlave[]> {
    return this.callDynamicAPI('package/getPkgSlave', params).pipe(
      map<DynamicApiResponse, ResPackageMasterSlave[]>((response) => {
        return response.body.map((data: any) => new ResPackageMasterSlave(data))
      })
    )
  }

  getPurchaseOrderForPackage(params: IResApiGetPurcahseOrderParams): Observable<ResPackagePurchaseOrder> {
    return this.callDynamicAPI('apc/getPkgPurchaseOrderID', params).pipe(
      map<DynamicApiResponse, ResPackagePurchaseOrder>((response) => new ResPackagePurchaseOrder(response.body[0]))
    );
  }

  getServiceAllocationForInspectModal(params: any): Observable<ReservationServiceAllocation[]> {
    return this.callDynamicAPI('package/getServiceAllocation', params).pipe(
      map<DynamicApiResponse, any>((res) => res.body.map((el: any) => new ReservationServiceAllocation(el)))
    );
  }

  public getServicesOptions(params?: IReservationApiGetOptions): Observable<ReservationServiceOption[]> {
    return this.callDynamicAPI('option/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationServiceOption>>((response) => {
        return response.body.map((o: IReservationApiServiceOption) => {
          let item = new ReservationServiceOption(o);
          return item;
        });
      })
    );
  }

  getTopicPackage(params: IResApiGetDayByDayTopicItineraryParams): Observable<ResPackageTopic[]> {
    return this.callDynamicAPI('package/getTopicPackage', params).pipe(
      map<DynamicApiResponse, ResPackageTopic[]>(
        (response) => response.body.map((data: any) => new ResPackageTopic(data))
      ));
  }

  loadDayByDayItinerary(packageID: number) {
    return this.callDynamicAPI('package/getDayByDay', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResPackageDayByDay>((response) => new ResPackageDayByDay(response.body))
    );
  }

  manageCommunicationEmail(params: IResApiPackageCommunicationEmail) {
    return this.postDynamicAPI('package/manageCommunicationEmail', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageDayByDayItinerary(params: IResApiManageDayByDayItinerary) {
    return this.postDynamicAPI('package/manageDayByDay', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageMiniPackage(params: IResApiManageMiniPackageParams): Observable<any> {
    return this.postDynamicAPI('package/manageMiniPackages', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  public managePackage(params: IReservationApiManagePackageParams): Observable<number> {
    return this.postDynamicAPI('package/manage', params).pipe(
      map<DynamicApiResponse, number>((response) => {
        if (Array.isArray(response.body)) {
          return response.body.length > 0 && response.body[0].PackageID ? response.body[0].PackageID : undefined;
        } else {
          return response.body.DT0 && response.body.DT0.length > 0 && response.body.DT0[0].PackageID ? response.body.DT0[0].PackageID : undefined;
        }
      })
    );
  }

  public managePackageAvailability(params: IReservationApiManagePackageAvailabilityParams): Observable<any> {
    return this.postDynamicAPI('package/manageAvailability', params);
  }

  public managePackageCancPolicy(params: IReservationApiManangePackageCanPolicyParams): Observable<any> {
    return this.postDynamicAPI('package/manageCancPolicy', params);
  }

  public managePackageDepScheduling(params: IReservationApiManagePackageDepSchedulingParams): Observable<any> {
    return this.postDynamicAPI('package/manageDepScheduling', params);
  }

  public managePackageElements(params: { PackageID: number, PackageOption: IReservationApiManagePackageOptionsParams[] }): Observable<any> {
    return this.postDynamicAPI('package/manageOptions', params);
  }

  managePackageHighlights(packageID: number, packageHighlights: IPackageHighlight[]) {
    return this.postDynamicAPI('package/highlightsManage', { PackageID: packageID, PackageHighlights: packageHighlights }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public managePackageItineraryDays(params: IReservationApiManagePackageItineraryDaysParams): Observable<any> {
    return this.postDynamicAPI('packageItineraryDay/manage', params);
  }

  managePackageItineraryDays2AKP(packageID: number, packageItineraryDay2AKP: IPackageItineraryDay2AKP[]) {
    return this.postDynamicAPI('package/packageItineraryDay2AKP', { PackageID: packageID, PackageItineraryDay2AKP: packageItineraryDay2AKP }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public managePackageLinkedPorts(params: IReservationApiManagePackageLinkedPortsParams): Observable<any> {
    return this.postDynamicAPI('package/managePorts', params);
  }

  public managePackageLinkedTours(params: IReservationApiManagePackageLinkedToursParams): Observable<any> {
    return this.postDynamicAPI('package/manageLinkedTours', params);
  }

  public managePackageLinkedVoyages(params: IReservationApiManagePackageLinkedVoyagesParams): Observable<any> {
    return this.postDynamicAPI('package/manageLinkedVoyages', params);
  }

  managePackagePrices(params: {
    PackageTravelDateID: number,
    currency: string,
    PriceTypes?: string,
    PackageOptionCost: IReservationApiPackageOptionCost[],
    PackagePrice: IReservationApiPackageOptionPriceParams[],
    PackageOptionUpgrade: IReservationApiPackageUpgradePriceParams[]
  }, mode: 'old' | 'new'): Observable<any> {

    params.PriceTypes = mode === 'old' ? undefined : `${params.PackagePrice[0].PriceTypeID}`
    const endpoint = mode === 'old' ? 'package/managePrices' : 'package/manageCosts'
    return this.postDynamicAPI(endpoint, params);
  }

  managePackagePricesSetup(params: IResApiManagePackagePricesSetup) {
    return this.postDynamicAPI('pkgPriceSetup/managePackagePrices', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  managePackageServiceAllocations(params: any) {
    return this.postDynamicAPI('package/managePackageAllocation', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  managePackageShipSetup(params: IResApiManagePackageShipSetupParams): Observable<any> {
    return this.postDynamicAPI('package/managePackageShip', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  managePackageSuiteCategoryStatus(params: IResApiManagePackageSuiteCategoryStatus): Observable<any> {
    return this.postDynamicAPI('package/managePackageSuiteCategoryStatus', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  managePackageTag(params?: IResApiManagePackageTagParams): Observable<any> {
    return this.postDynamicAPI('package/managePackageTag', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  managePackageTravelDateRelease(params: IResApiManagePackageTravelDateRelease) {
    return this.postDynamicAPI('package/manageTravelDateRelease', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public managePackageTravelDates(params: IReservationApiManagePackageTravelDatesParams): Observable<any> {
    return this.postDynamicAPI('packageTravelDate/manage', params);
  }

  public managePackageUpgrades(params: IReservationApiManagePackageUpgradeParams): Observable<any> {
    return this.postDynamicAPI('packageUpgrade/manage', params);
  }

  public managePkgEResAllocation(params: IResApiPkgEResAllocationManageParams): Observable<any> {
    return this.postDynamicAPI('package-setup/manageEresAllocation', params);
  }

  managePkgMasterSlave(params: IResApiManagePkgMasterSlaveParams) {
    return this.postDynamicAPI('package/managePkgMasterSlave', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  managePromoPackages(params?: IResApiManagePromoPackages) {
    return this.postDynamicAPI('promo/managePromoPackages', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageTopicPackage(params: IResApiManageTopicPackageParams): Observable<any> {
    return this.postDynamicAPI('package/manageTopicPackage', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  packageManageSingleSupp(params: IResPackageSetupSingleSuppManage): Observable<any> {
    return this.postDynamicAPI('package/manageSingleSupplement', params);
  }

  packageOptionCostBandGet(params: IResPackageCostBandGetParams) {
    return this.callDynamicAPI('package/costBandGet', params).pipe(
      map<DynamicApiResponse, PackageOptionCostBand[]>((response) => {
        return response.body.map((data: any) => new PackageOptionCostBand(data))
      })
    )
  }

  packageOptionCostBandManage(params: IResPackageCostBandManageParams) {
    return this.callDynamicAPI('package/costBandManage', params, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )

  }

  public packagePricesCloneDates(params: IReservationApiPricesCloneDatesParams): Observable<any> {
    return this.postDynamicAPI('package/CostPriceClone', params);
  }

  public packagePricesCloneUpgrade(params: IReservationApiPricesCloneUpgradeParams): Observable<any> {
    return this.postDynamicAPI('package/CostUpgradeClone', params);
  }

  packageSwap(params: IResApiPackageSwapParams) {
    return this.postDynamicAPI('package/swap', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  queryDayByDayUpdateBkg(params: IResApiDayByDayImportFromWebsite) {
    return this.getDynamicAPI('package/queryDayByDayBkg', params).pipe(
      map<DynamicApiResponse, ResBkgList[]>((response) => {
        return response.body.map((data: any) => new ResBkgList(data));
      })
    )
  }

  resetTopicPackage(packageID: number): Observable<any> {
    return this.callDynamicAPI('package/resetTopicPackage', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  public saveBookingCustomRequest(req: IReservationApiSaveCustomRequest) {
    return this.postDynamicAPI('customRequest/save', req).pipe(
      map<DynamicApiResponse, number>((response) =>
        !this.isEmpty(response.body) ? response.body.RequestID : null
      )
    );
  }

  syncPkgECM(params: IResApiSyncPkgECMParams): Observable<any> {
    return this.postDynamicAPI('package/managePkgEMCSync', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }
}
